import { GetStaticPropsContext } from "next"
import getT from "next-translate/getT"

/**
 * Default meta compilation — every page's SEOProps funnels through here
 * (CMS pages pass their metaTitle/metaDescription/metaImage on top).
 *
 * 2026-09-07 fixes over the v4-inherited version:
 *  - the OG image default pointed at HESTAFTA.ORG's logo (copied from that
 *    project) — every share card without an explicit image advertised the
 *    wrong site. Now the brand card at /images/og-image.jpg (1200×630 —
 *    designer's shockwave-session card, swapped in 2026-09-17; the extension
 *    change also busts scraper caches from earlier shares).
 *  - `keywords` dropped: it read the nonexistent `website.keywords` locale
 *    key (producing the literal key string) and meta.tsx never rendered it —
 *    the keywords meta tag has been dead SEO weight for a decade anyway.
 *  - og:locale values in the CORRECT casing (el_GR / en_GB, not el_gr) and
 *    actually derived from the locale — meta.tsx used to hardcode el_GR, so
 *    English pages claimed Greek.
 */
export interface SEOProps {
  pageTitle: string,
  description: string,
  openGraphWebsiteTitle: string,
  openGraphTitle: string,
  openGraphDescription: string,
  openGraphType: string,
  openGraphLocale: 'el_GR' | 'en_GB',
  openGraphImageUrl: string,
  /** `<meta name="robots">` — from shared.seo metaRobots (e.g. «noindex, follow»
   *  on the thank-you page); absent = indexable, no tag emitted */
  metaRobots?: string,
  /** canonical override — from shared.seo canonicalURL; absent = self-canonical */
  canonicalUrl?: string,
  /** extra JSON-LD — shared.seo structuredData passes through verbatim, so an
   *  admin can attach page-specific schema without a deploy */
  structuredData?: object,
}

const OG_IMAGE_DEFAULT = 'https://myphysiowave.gr/images/og-image.jpg'

export async function compileDefaultSEOInformation(context: GetStaticPropsContext, props:
  { pageTitle?: string,
    pageGroupTitle?: string,
    /** CMS metaTitles arrive fully branded («… | Physiowave») — set this so
     *  the site-name suffix is NOT bolted on a second time */
    titleIsComplete?: boolean,
    description?: string,
    openGraphType?: string,
    openGraphImageUrl?: string,
    metaRobots?: string,
    canonicalUrl?: string,
    structuredData?: object,
  }): Promise<SEOProps> {
  const t = await getT(context.locale, 'main')

  const openGraphWebsiteShortTitle = t('website.title')
  const websiteMoto = t('website.tagLine')
  const openGraphWebsiteTitle = `${openGraphWebsiteShortTitle}${websiteMoto ? ` - ${websiteMoto}` : ''}`

  // with a real page title the suffix is the site name alone — the tagline
  // in every tab title just eats the SERP's 60 characters; the full branded
  // string remains the bare-fallback title
  const title = props.titleIsComplete && props.pageTitle
    ? props.pageTitle
    : props.pageTitle || props.pageGroupTitle
      ? `${props.pageTitle ? `${props.pageTitle} - ` : ''}${props.pageGroupTitle ? `${props.pageGroupTitle} | ` : ''}${openGraphWebsiteShortTitle}`
      : openGraphWebsiteTitle

  return {
    pageTitle: title,
    description: props.description || t('website.description'),
    openGraphTitle: title,
    openGraphDescription: props.description || t('website.description'),
    openGraphWebsiteTitle,
    openGraphType: props.openGraphType || 'website',
    openGraphImageUrl: props.openGraphImageUrl || OG_IMAGE_DEFAULT,
    openGraphLocale: context.locale === 'en' ? 'en_GB' : 'el_GR',
    ...(props.metaRobots ? { metaRobots: props.metaRobots } : {}),
    ...(props.canonicalUrl ? { canonicalUrl: props.canonicalUrl } : {}),
    ...(props.structuredData ? { structuredData: props.structuredData } : {}),
  }
}

/**
 * SEO for an entity page resolved by a shared shell (treatments, conditions,
 * team members, posts — D35): the shell web-page carries a GENERIC metaTitle
 * («Θεραπεία | Physiowave»), which left every entity of a type with the same
 * duplicate title. The entity's own seo component wins when the editor filled
 * it; otherwise title + teaser build one.
 */
export async function compileEntitySEOInformation(
  context: GetStaticPropsContext,
  entity: {
    title?: string
    displayName?: string
    teaser?: string
    /** entity photography — the share-card fallback when no explicit
     *  metaImage is set: a treatment's cover or a post's featured image beats
     *  the generic brand card on every share */
    coverImage?: { url?: string }
    featuredImage?: { url?: string }
    photo?: { url?: string }
    seo?: {
      metaTitle?: string; metaDescription?: string; metaImage?: { url?: string }
      metaRobots?: string; canonicalURL?: string; structuredData?: object
    }
  },
  extra?: { openGraphType?: string }
): Promise<SEOProps> {
  return compileDefaultSEOInformation(context, {
    pageTitle: entity.seo?.metaTitle || entity.title || entity.displayName || '',
    titleIsComplete: Boolean(entity.seo?.metaTitle),
    description: entity.seo?.metaDescription || entity.teaser,
    openGraphImageUrl: entity.seo?.metaImage?.url
      || entity.featuredImage?.url || entity.coverImage?.url || entity.photo?.url,
    openGraphType: extra?.openGraphType,
    metaRobots: entity.seo?.metaRobots,
    canonicalUrl: entity.seo?.canonicalURL,
    structuredData: entity.seo?.structuredData,
  })
}
